import { BrowserRouter, Route, Routes } from 'react-router';
import { lazy, Suspense } from 'react';
import './App.css';
import LayoutProvider from './context';
import MoonMeetProvider from './moonmeet/MoonMeetProvider';
import AppearanceProvider from './theme/AppearanceProvider';
import { INITIAL_APPEARANCE, JOY_MODE_KEY } from './theme/appearance';
import './theme/appearance.css';
import { CssBaseline, CssVarsProvider, extendTheme, CircularProgress, Box } from '@mui/joy';

import KanbanDashboard from './pages/KanbanDashboard';
import LKPage from './pages/LKPage';
import KanbanPage from './pages/KanbanPage';
import NotesPage from './pages/NotesPage';
import TasksPage from './pages/TasksPage';
import FinancePage from './pages/FinancePage';
import GuestTasksPage from './pages/GuestTasksPage';
import ShareModal from './components/modals/ShareModal';
import ModalProfile from './components/modals/ModalProfile';

const MoonMeetPage = lazy(() => import('./moonmeet/MoonMeetPage'));
const MoonMeetPublicPage = lazy(() => import('./moonmeet/MoonMeetPage').then(m => ({ default: m.MoonMeetPublicPage })));
const DrawerPage = lazy(() => import('./pages/DrawerPage'));
const DrawerPublicPage = lazy(() => import('./pages/DrawerPublicPage'));

const SafePage = lazy(() => import('./pages/SafePage'));



const darkDisabled = {
  plainDisabledColor: '#77737f',
  outlinedDisabledColor: '#77737f', outlinedDisabledBorder: '#38343e',
  softDisabledColor: '#77737f', softDisabledBg: '#29262e',
  solidDisabledColor: '#77737f', solidDisabledBg: '#29262e',
};

const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          50:  '#f0f0fe',  // почти белый с оттенком
          100: '#e2e3fd',  // soft вариант фон
          200: '#c8cafc',  // hover на soft кнопке
          300: '#a5a8f9',  // outlined бортики
          400: '#868af6',  // промежуточный
          500: '#686DF3',  // основной — все solid варианты
          600: '#5055d9',  // hover на solid
          700: '#3d42c0',  // нажатие на solid
          800: '#2e32a6',  // тёмный акцент
          900: '#20258c',  // самый тёмный
        },
        danger: {
          50: "#fde3e4",
          100: "#facccd",
          200: "#f49999",
          300: "#e0666b",
          400: "#cc3340",
          500: "#c30013",
          600: "#b10011",
          700: "#9e000e",
          800: "#8b000b",
          900: "#780008"
        },
        success: {
          50: "#F6FEF6",
          100: "#E3FBE3",
          200: "#C7F7C7",
          300: "#A1E8A1",
          400: "#51BC51",
          500: "#1F7A1F",
          600: "#136C13",
          700: "#0A470A",
          800: "#042F04",
          900: "#021D02"
        },
        neutral: {
          100: '#f5f7faf8',
          500: '#242424', // Цвет фона для всех вариантов
          600: '#2e2e2e', // Цвет фона при наведении если solid вариант
          700: '#1f1f1f', // Цвет фона для всех вариантов (при нажатии)
        },
      },
    },
    dark: {
      shadowChannel: '151 110 201',
      shadowOpacity: .14,
      shadowRing: '0 0 0 1px rgba(179,142,220,.035)',
      palette: {
        background: { body: '#101012', surface: '#1b1b1f', popup: '#242328', level1: '#161619', level2: '#252429', level3: '#302e35', tooltip: '#39353f', backdrop: 'rgba(0,0,0,.68)' },
        text: { primary: '#efedf3', secondary: '#c2becb', tertiary: '#a39eaf', icon: '#bdb6ca' },
        divider: 'rgba(187,165,218,.15)',
        primary: { ...darkDisabled, 50: '#211d2b', 100: '#2d253c', 200: '#443454', 300: '#8967b8', 400: '#b997ef', 500: '#ad89ea', 600: '#9974d5', 700: '#cbb0f5', 800: '#e1cef9', 900: '#f3ebff', plainColor: '#c8a8f5', plainHoverColor: '#e4d3fb', plainActiveColor: '#f2e8ff', outlinedHoverColor: '#e4d3fb', outlinedActiveColor: '#f2e8ff', softHoverColor: '#e4d3fb', softActiveColor: '#f2e8ff', plainHoverBg: '#30263f', plainActiveBg: '#40314f', softBg: '#2c2339', softColor: '#d7bdf7', softHoverBg: '#3a2d4a', softActiveBg: '#4a385e', outlinedColor: '#c8a8f5', outlinedBorder: '#68527d', outlinedHoverBg: '#2c2339', solidBg: '#8058b6', solidColor: '#fff', solidHoverBg: '#9168c7', solidActiveBg: '#7651a8' },
        neutral: { ...darkDisabled, 50: '#161619', 100: '#1f1e23', 200: '#302e36', 300: '#4a4652', 400: '#8b8495', 500: '#a9a1b3', 600: '#c4bdce', 700: '#d7d0e0', 800: '#e4dfea', 900: '#f3eff7', plainColor: '#cbc4d5', plainHoverColor: '#efedf3', plainActiveColor: '#f3eff7', outlinedHoverColor: '#efedf3', outlinedActiveColor: '#f3eff7', softHoverColor: '#efedf3', softActiveColor: '#f3eff7', solidActiveBg: '#aaa0b7', plainHoverBg: '#2b282f', plainActiveBg: '#37323e', softBg: '#27242d', softColor: '#ded7e7', softHoverBg: '#342e3c', softActiveBg: '#42394c', outlinedColor: '#ded7e7', outlinedBorder: '#49414f', outlinedHoverBg: '#2b282f', solidBg: '#c3b9cf', solidColor: '#18151d', solidHoverBg: '#d7cce3' },
        success: { ...darkDisabled, 50: '#142c28', 100: '#1b3830', 200: '#244b3e', 300: '#3b7860', 400: '#69c29e', 500: '#80d5ae', plainColor: '#8fdbb8', softColor: '#a1e4c5', softBg: '#193b30', softHoverBg: '#24503f', solidBg: '#388365', solidColor: '#fff' },
        danger: { ...darkDisabled, 50: '#35212a', 100: '#462833', 200: '#613542', 300: '#a85b6b', 400: '#ed93a3', 500: '#f2a5b2', plainColor: '#f4a7b5', softColor: '#f8bdc7', softBg: '#422733', softHoverBg: '#55323e', solidBg: '#b84964', solidColor: '#fff' },
        warning: { ...darkDisabled, 50: '#302919', 100: '#40331f', 200: '#5b4729', 300: '#997840', 400: '#dbb16b', 500: '#edc582', plainColor: '#edc582', softColor: '#f4d49c', softBg: '#3d3120', softHoverBg: '#51402a', solidBg: '#997439', solidColor: '#fff' },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      
      extrasmall: 300,
      small: 500,
      medium: 800,
      large: 1100,
    },
  },
  components: {
    JoyButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: '.2s',
          '& svg': { color: 'inherit' },
          borderRadius: theme.vars.radius.lg,
          "&:hover": {
            transform: "scale(1.02)"
          }
        })
      }
    },
    JoyTextarea: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.vars.radius.lg
        })
      }
    },
    JoyInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.vars.radius.lg
        })
      }
    },
    JoySelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.vars.radius.lg
        })
      }
    },
    JoyAutocomplete: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.vars.radius.lg
        })
      }
    },
    JoyModalDialog: { styleOverrides: { root: { backgroundColor: 'var(--ld-panel)' } } },
    JoyIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.vars.radius.lg,
          '& svg': { color: 'inherit' }
        })
      }
    }
  },
});



// const ProtectedRoute = ({ children, allowedRoles, userRole }) => {
//   const navigate = useNavigate();
//   const location = useLocation();

//   useEffect(() => {
//     // Защита от ошибки, если `userRole` нет или это пустой массив
//     if (!userRole || !Array.isArray(userRole) || userRole.length === 0) return;
    
//     // Проверяем, есть ли хотя бы одна роль пользователя в allowedRoles
//     const hasAccess = userRole.some(role => allowedRoles.includes(role));
    
//     if (!hasAccess) {
//       if (!location.key) {
//         navigate("/", { replace: true });
//       }
//     }
    
//     console.log("Allowed roles:", allowedRoles);
//     console.log("User roles:", userRole);
//     console.log("Has access:", hasAccess);
//   }, [location.pathname, allowedRoles, userRole, navigate, location.key]);
      
//   // Проверяем доступ для рендера
//   const hasAccess = Array.isArray(userRole) && userRole.length > 0 && 
//                    userRole.some(role => allowedRoles.includes(role));
  
//   return hasAccess ? children : null;
// };


function AnimatePage() {
  // Keep the LK shell mounted. Route rendering must not wait for a dormant
  // financial panel to report an exit animation that never started.
  return (
      <Routes>
        {/* Гостевой доступ — без шапки и сайдбара */}
        <Route path="/moonmeet/join/:invite" element={<Suspense fallback={<Box sx={{p:4}}>Открываем MoonMeet…</Box>}><MoonMeetPublicPage/></Suspense>}/>
        <Route path="/drawer/share" element={<Suspense fallback={<Box sx={{p:4}}>Открываем доску…</Box>}><DrawerPublicPage /></Suspense>} />
        <Route path="/share/:token" element={<GuestTasksPage />} />

        {/* Основное приложение */}
        <Route path='/' element={<LKPage />}>
          <Route path="" element={<KanbanDashboard />} />
          <Route path="kanban" element={<KanbanPage />} />
          <Route path="notes" element={<NotesPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="finances" element={<FinancePage />} />
          <Route path="moonmeet" element={<Suspense fallback={<Box sx={{p:4}}>Открываем MoonMeet…</Box>}><MoonMeetPage/></Suspense>}/>
          <Route path="drawer" element={<Suspense fallback={<Box sx={{p:4}}>Открываем Drawer…</Box>}><DrawerPage /></Suspense>} />
          <Route path="safe" element={<Suspense fallback={<Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size="sm" /></Box>}><SafePage /></Suspense>} />
        </Route>
      </Routes>
  );
}


function App() {
  return (
    <BrowserRouter>
      <CssVarsProvider
        theme={theme}
        defaultMode={INITIAL_APPEARANCE.colorScheme}
        modeStorageKey={JOY_MODE_KEY}
      >
        <CssBaseline />
        <AppearanceProvider>
        <LayoutProvider>
        <MoonMeetProvider>
          <AnimatePage/>

          <ShareModal />
          <ModalProfile />
        </MoonMeetProvider>
        </LayoutProvider>
        </AppearanceProvider>
      </CssVarsProvider>
    </BrowserRouter>
  );
}

export default App;
